import type { BitcoinTransactionInput } from '@txviz/core';
import { hexToBytes } from '@txviz/core';
import { hasWitness } from './types.js';

/**
 * Serialize a Bitcoin transaction to its canonical wire format.
 *
 * For TXID computation (hash verification), we always serialize
 * in the **legacy** (non-witness) format, even for SegWit transactions.
 * This is because the TXID is defined as the double-SHA256 of the
 * legacy serialization (without marker, flag, and witness fields).
 *
 * If rawHex is provided, we use it directly (after validation).
 * Otherwise, we reconstruct from the structured fields.
 */
export function serializeBitcoinTransaction(tx: BitcoinTransactionInput): Uint8Array {
  if (tx.rawHex) {
    return hexToBytes(tx.rawHex);
  }

  return serializeWithoutWitness(tx);
}

/**
 * Serialize in legacy format (no marker, flag, or witness).
 * This produces the data that gets double-SHA256'd to compute the TXID.
 */
function serializeWithoutWitness(tx: BitcoinTransactionInput): Uint8Array {
  const parts: Uint8Array[] = [];

  // Version (4 bytes, little-endian)
  parts.push(writeUint32LE(tx.version));

  // Input count (varint)
  parts.push(writeVarInt(tx.inputs.length));

  // Inputs
  for (const input of tx.inputs) {
    // Previous output hash (32 bytes, reversed for internal byte order)
    parts.push(hexToBytes(reverseHex(input.txid)));
    // Previous output index (4 bytes, little-endian)
    parts.push(writeUint32LE(input.vout));
    // Script length + script
    const scriptBytes = hexToBytes(input.scriptSig);
    parts.push(writeVarInt(scriptBytes.length));
    parts.push(scriptBytes);
    // Sequence (4 bytes, little-endian)
    parts.push(writeUint32LE(input.sequence));
  }

  // Output count (varint)
  parts.push(writeVarInt(tx.outputs.length));

  // Outputs
  for (const output of tx.outputs) {
    // Value (8 bytes, little-endian)
    parts.push(writeUint64LE(output.value));
    // Script length + script
    const scriptBytes = hexToBytes(output.scriptPubKey);
    parts.push(writeVarInt(scriptBytes.length));
    parts.push(scriptBytes);
  }

  // Locktime (4 bytes, little-endian)
  parts.push(writeUint32LE(tx.locktime));

  return concatBytes(parts);
}

/**
 * Serialize with witness data (for computing WTXID and full wire format).
 */
export function serializeWithWitness(tx: BitcoinTransactionInput): Uint8Array {
  if (!hasWitness(tx)) {
    return serializeWithoutWitness(tx);
  }

  const parts: Uint8Array[] = [];

  // Version (4 bytes, little-endian)
  parts.push(writeUint32LE(tx.version));

  // Marker (0x00) and Flag (0x01)
  parts.push(new Uint8Array([0x00, 0x01]));

  // Input count (varint)
  parts.push(writeVarInt(tx.inputs.length));

  // Inputs
  for (const input of tx.inputs) {
    parts.push(hexToBytes(reverseHex(input.txid)));
    parts.push(writeUint32LE(input.vout));
    const scriptBytes = hexToBytes(input.scriptSig);
    parts.push(writeVarInt(scriptBytes.length));
    parts.push(scriptBytes);
    parts.push(writeUint32LE(input.sequence));
  }

  // Output count (varint)
  parts.push(writeVarInt(tx.outputs.length));

  // Outputs
  for (const output of tx.outputs) {
    parts.push(writeUint64LE(output.value));
    const scriptBytes = hexToBytes(output.scriptPubKey);
    parts.push(writeVarInt(scriptBytes.length));
    parts.push(scriptBytes);
  }

  // Witness data
  for (const input of tx.inputs) {
    const witness = input.witness ?? [];
    parts.push(writeVarInt(witness.length));
    for (const item of witness) {
      const itemBytes = hexToBytes(item);
      parts.push(writeVarInt(itemBytes.length));
      parts.push(itemBytes);
    }
  }

  // Locktime (4 bytes, little-endian)
  parts.push(writeUint32LE(tx.locktime));

  return concatBytes(parts);
}

// ── Helper functions ───────────────────────────────────────────────

function writeUint32LE(value: number): Uint8Array {
  const buf = new Uint8Array(4);
  const view = new DataView(buf.buffer);
  view.setUint32(0, value, true); // little-endian
  return buf;
}

function writeUint64LE(value: bigint): Uint8Array {
  const buf = new Uint8Array(8);
  const view = new DataView(buf.buffer);
  view.setBigUint64(0, value, true); // little-endian
  return buf;
}

function writeVarInt(value: number): Uint8Array {
  if (value < 0xfd) {
    return new Uint8Array([value]);
  }
  if (value <= 0xffff) {
    const buf = new Uint8Array(3);
    buf[0] = 0xfd;
    const view = new DataView(buf.buffer);
    view.setUint16(1, value, true);
    return buf;
  }
  if (value <= 0xffffffff) {
    const buf = new Uint8Array(5);
    buf[0] = 0xfe;
    const view = new DataView(buf.buffer);
    view.setUint32(1, value, true);
    return buf;
  }
  const buf = new Uint8Array(9);
  buf[0] = 0xff;
  const view = new DataView(buf.buffer);
  view.setBigUint64(1, BigInt(value), true);
  return buf;
}

/**
 * Reverse a hex string (swap byte order).
 * Bitcoin txids are displayed in reverse byte order.
 */
function reverseHex(hex: string): string {
  const clean = hex.startsWith('0x') ? hex.slice(2) : hex;
  const pairs: string[] = [];
  for (let i = clean.length - 2; i >= 0; i -= 2) {
    pairs.push(clean.substring(i, i + 2));
  }
  return pairs.join('');
}

function concatBytes(arrays: Uint8Array[]): Uint8Array {
  let totalLength = 0;
  for (const arr of arrays) {
    totalLength += arr.length;
  }
  const result = new Uint8Array(totalLength);
  let offset = 0;
  for (const arr of arrays) {
    result.set(arr, offset);
    offset += arr.length;
  }
  return result;
}
